import { computed, type Ref } from 'vue';
import { useCommonStore } from '~/stores/useCommonStore';

type OptionValue = string | number | null | undefined;

type SharedOption = {
  value?: OptionValue;
  id?: OptionValue;
  codeId?: OptionValue;
  evalCycleCd?: OptionValue;
  totDegreeId?: OptionValue;
};

type SyncSelectionParams<T> = {
  options: T[];
  selectedValue: Ref<any>;
  sharedValue: string | null | undefined;
  setSharedValue: (value: string) => void;
  getValue?: (option: T) => OptionValue;
  emptyValue?: string;
};

const normalizeValue = (value: OptionValue) => String(value ?? '');

export const getHrEvalOptionValue = <T extends SharedOption>(option: T): string =>
  normalizeValue(option.value ?? option.id ?? option.codeId ?? option.evalCycleCd ?? option.totDegreeId);

export const resolveSharedOptionValue = <T>(
  options: T[],
  sharedValue: string | null | undefined,
  getValue: (option: T) => OptionValue,
  emptyValue = '',
) => {
  if (!options.length) return emptyValue;

  const shared = String(sharedValue ?? '');
  const matched = options.find(option => normalizeValue(getValue(option)) === shared);
  return normalizeValue(getValue(matched ?? options[0]));
};

export const syncSharedSelection = <T>({
  options,
  selectedValue,
  sharedValue,
  setSharedValue,
  getValue = option => getHrEvalOptionValue(option as SharedOption),
  emptyValue = '',
}: SyncSelectionParams<T>) => {
  const nextValue = resolveSharedOptionValue(options, sharedValue, getValue, emptyValue);

  selectedValue.value = nextValue;
  setSharedValue(nextValue);

  return nextValue;
};

export const useHrEvaluationSharedFilter = () => {
  const sharedFindYear = computed({
    get: () => String(useCommonStore().findYear ?? ''),
    set: value => {
      useCommonStore().findYear = value;
    },
  });

  const sharedEvalCycleCd = computed({
    get: () => String(useCommonStore().findEvalCycleCd ?? ''),
    set: value => {
      useCommonStore().findEvalCycleCd = value;
    },
  });

  const sharedTotDegreeId = computed({
    get: () => String(useCommonStore().findTotDegreeId ?? ''),
    set: value => {
      useCommonStore().findTotDegreeId = value;
    },
  });

  return {
    sharedFindYear,
    sharedEvalCycleCd,
    sharedTotDegreeId,
    syncYear: <T extends SharedOption>(options: T[], selectedValue: Ref<any>) =>
      syncSharedSelection({
        options,
        selectedValue,
        sharedValue: sharedFindYear.value,
        setSharedValue: value => {
          sharedFindYear.value = value;
        },
      }),
    syncEvalCycle: <T extends SharedOption>(options: T[], selectedValue: Ref<any>) =>
      syncSharedSelection({
        options,
        selectedValue,
        sharedValue: sharedEvalCycleCd.value,
        setSharedValue: value => {
          sharedEvalCycleCd.value = value;
        },
      }),
    syncTotDegree: <T extends SharedOption>(options: T[], selectedValue: Ref<any>) =>
      syncSharedSelection({
        options,
        selectedValue,
        sharedValue: sharedTotDegreeId.value,
        setSharedValue: value => {
          sharedTotDegreeId.value = value;
        },
      }),
  };
};
